import { find } from 'lodash';

import { Mention } from '../../../Common/Mentions/models/mention';
import { MentionSearchModel } from './mention-search.model';
import { SaveMentionsRequestModel } from '../../../api/mentions/model/save-mentions-request.model';
import { MentionModel } from '../../../api/mentions/model/mention.model';
import { BulkUpdateMentionRequestModel } from '../../../api/mentions/model/bulk-update-mention-request.model';
import { MentionIdMercuryMapModel } from '../../../api/mentions/model/mention-id-mercury-map.model';
import { SaveMentionsApiModel } from '../../../api/mentions/model/save-mentions-api.model';

export class SaveMentionsModel {
    public search: MentionSearchModel;
    public folderId: number;
    public folderIds?: number[];
    public mentions: Mention[];

    constructor(mentions: Mention[], search?: MentionSearchModel) {
        this.mentions = mentions;
        this.search = search;
    }

    /**
     * Returns the mentions converted to MentionModels
     */
    getMentionModels(): MentionModel[] {
        return this.mentions.map((mention) => {
            return mention.convertToApiMention();
        });
    }

    /**
     * Returns the mentions in this model that are already saved to user account
     */
    getAlreadySavedMentions(): Mention[] {
        return this.mentions.filter(mention => {
            if (mention.mentionId) {
                return true;
            }

            return false;
        });
    }

    /**
     * Returns the mentions in this model that are not yet saved to user account
     */
    getNewMentions(): Mention[] {
        return this.mentions.filter(mention => {
            if (!mention.mentionId) {
                return true;
            }

            return false;
        });
    }

    /**
     * Returns the MentionIdMercuryMapModels for mentions in this model that are already saved
     */
    getMentionIdMercuryMapForExistingMentions(): MentionIdMercuryMapModel[] {
        return this.getAlreadySavedMentions().map(mention => {
            return {
                mentionId: mention.mentionId,
                mercuryId: mention.id
            };
        });
    }

    /**
     * Returns api model for
     */
    getSaveNewMentionsRequestModel(forceSyndicateSave?: boolean): SaveMentionsRequestModel {
        const saveMentionsApiModel = {};

        if (this.search) {
            const searchQueryApiModel = this.search.searchCriteria.query.getApiEntity();

            if (searchQueryApiModel.keywordGroups?.length > 0) {
                saveMentionsApiModel['context'] = { 'keywordGroups': searchQueryApiModel.keywordGroups };
            } else if (searchQueryApiModel.advancedBooleanExpressionFilter) {
                saveMentionsApiModel['context'] = { 'advancedBooleanExpressionFilter': searchQueryApiModel.advancedBooleanExpressionFilter };
            }
        }

        if (this.folderId !== null && this.folderId !== undefined) {
            saveMentionsApiModel['folderId'] = this.folderId;
        }
        if (this.folderIds?.length > 0) {
            saveMentionsApiModel['folderIds'] = this.folderIds;
        }
        if (this.mentions?.some(m => !!m.syndicateInfo) || forceSyndicateSave) {
            if (!saveMentionsApiModel['context']) {
                saveMentionsApiModel['context'] = {};
            }
            saveMentionsApiModel['context']['isSyndicated'] = true;
        }
        saveMentionsApiModel['mercuryIds'] = this.getMentionModels().filter(mention => {
            if (!mention.mercuryId || mention.id) {
                return false;
            }

            return true;
        }).map((mention) => {
            return mention.mercuryId;
        });

        if (this.mentions) {
            saveMentionsApiModel['mercuryIds'].forEach(mercuryId => {
                const mention = find(this.mentions, { 'id': mercuryId });
                if (mention.translation) {
                    if (!saveMentionsApiModel['translations']) {
                        saveMentionsApiModel['translations'] = {};
                    }
                    saveMentionsApiModel['translations'][mercuryId] = mention.toUpdateMentionTranslationApiModel().translation;
                }
            });
        }

        return <SaveMentionsRequestModel>saveMentionsApiModel;
    }

    /**
     * Returns api model for saving mentions to folder
     */
    getAddSavedMentionsToFolderApiModel(): BulkUpdateMentionRequestModel {
        const savingToFolder = (this.folderId !== null && this.folderId !== undefined);
        const savingToFolders = this.folderIds?.length > 0;
        const bulkMentionIds = [];

        this.getMentionModels().forEach(mention => {
            if (!mention.mercuryId) {
                bulkMentionIds.push(mention.id);
            } else if (mention.id && (savingToFolder || savingToFolders)) {
                bulkMentionIds.push(mention.id);
            }
        });

        return {
            modifications: savingToFolder
                ? { addFolder: this.folderId }
                : savingToFolders
                    ? {addFolders: this.folderIds}
                    : null,
            mentionIds: bulkMentionIds
        };
    }

    /**
     * Returns the api model for saving mentions
     */
    toApiModel(forceSyndicateSave?: boolean): SaveMentionsApiModel {
        return {
            newMentionsRequest: this.getSaveNewMentionsRequestModel(forceSyndicateSave),
            existingMentionsToFolderRequest: this.getAddSavedMentionsToFolderApiModel(),
            existingMentionMappings: this.getMentionIdMercuryMapForExistingMentions()
        };
    }
}
